/**
 * Declarative social action vocabulary, shared by humans (Web buttons) and agents (text lines).
 *
 * The MVP keeps to one principle from the roadmap: humans and agents act through the *same* verbs, so an action an
 * agent emits as text is exactly an action a Web control performs. [ActionGrammar] parses one action per line into a
 * [SocialAction]; [perform] resolves its [SocialRef]s against a [SocialBoard] and runs it through the node's venue,
 * which appends and publishes a commitment just like any local authoring call.
 */

package com.oasisfeng.invaluable.social

import com.oasisfeng.invaluable.Quantity

/** A single declarative action over the local social world. */
sealed interface SocialAction {
    val cost: Quantity

    data class PostText(val text: String, override val cost: Quantity) : SocialAction
    data class Comment(val on: SocialRef, val text: String, override val cost: Quantity) : SocialAction
    data class Reply(val via: SocialRef, val text: String, override val cost: Quantity) : SocialAction
    data class Recommend(val post: SocialRef, val to: SocialRef, override val cost: Quantity) : SocialAction
    data class Endorse(val route: SocialRef, override val cost: Quantity) : SocialAction
    data class Back(val target: SocialRef, override val cost: Quantity) : SocialAction
}

/** Outcome of parsing one action line. */
sealed interface ParsedAction {
    data class Ok(val action: SocialAction) : ParsedAction
    data class Error(val message: String) : ParsedAction
}

/** Outcome of performing one action against a node. */
sealed interface ActionOutcome {
    /**
     * A successful action. [ref] names the object the caller most likely acts on next — the post an authoring verb just
     * created, the route a recommend opened, or the target a back/endorse moved — so a tool front-end can echo its
     * resulting state as a receipt instead of forcing a follow-up `get_context` read.
     */
    data class Done(val message: String, val ref: SocialRef? = null) : ActionOutcome
    data class Failed(val message: String) : ActionOutcome
}

/**
 * Line grammar for [SocialAction]s. One action per line; `#` lines and blanks are ignored by [parseAll].
 *
 * ```text
 * post <cost> <text...>
 * comment <postRef> <cost> <text...>
 * reply <routeRef> <cost> <text...>
 * recommend <postRef> <actorRef> <cost>
 * endorse <routeRef> <cost>
 * back <ref> <cost>
 * ```
 *
 * `<cost>` is an integer (`10`) or a fraction (`1/2`); references are the content-addressed handles from a [SocialBoard].
 */
object ActionGrammar {

    /** Grammar lines, surfaced verbatim to agents by [ContextRenderer] and to humans by the TUI `help`. */
    val syntax: List<String> = listOf(
        "post <cost> <text...>",
        "comment <postRef> <cost> <text...>",
        "reply <routeRef> <cost> <text...>",
        "recommend <postRef> <actorRef> <cost>",
        "endorse <routeRef> <cost>",
        "back <ref> <cost>",
    )

    /** Parse every non-blank, non-comment line. */
    fun parseAll(text: String): List<ParsedAction> =
        text.lineSequence().map(String::trim).filter { it.isNotEmpty() && !it.startsWith("#") }.map(::parse).toList()

    fun parse(line: String): ParsedAction {
        val tokens = line.trim().split(Regex("\\s+"))
        val verb = tokens.firstOrNull()?.lowercase() ?: return err("empty action")
        val args = tokens.drop(1)
        return when (verb) {
            "post" -> withCostThenText(args, 0) { text, cost -> SocialAction.PostText(text, cost) }
            "comment" -> withRefCostText(args, "comment <postRef> <cost> <text...>") { ref, text, cost ->
                SocialAction.Comment(SocialRef(ref), text, cost) }
            "reply" -> withRefCostText(args, "reply <routeRef> <cost> <text...>") { ref, text, cost ->
                SocialAction.Reply(SocialRef(ref), text, cost) }
            "recommend" -> {
                if (args.size != 3) return err("usage: recommend <postRef> <actorRef> <cost>")
                val cost = quantityOrNull(args[2]) ?: return err("bad cost '${args[2]}'")
                ParsedAction.Ok(SocialAction.Recommend(SocialRef(args[0]), SocialRef(args[1]), cost))
            }
            "endorse" -> refThenCost(args, "endorse <routeRef> <cost>") { ref, cost -> SocialAction.Endorse(SocialRef(ref), cost) }
            "back" -> refThenCost(args, "back <ref> <cost>") { ref, cost -> SocialAction.Back(SocialRef(ref), cost) }
            else -> err("unknown action '$verb' (known: ${syntax.joinToString(", ") { it.substringBefore(' ') }})")
        }
    }

    private inline fun withCostThenText(args: List<String>, costIndex: Int, build: (String, Quantity) -> SocialAction): ParsedAction {
        val cost = args.getOrNull(costIndex)?.let(::quantityOrNull) ?: return err("usage: post <cost> <text...>")
        val text = args.drop(costIndex + 1).joinToString(" ").trim()
        if (text.isEmpty()) return err("post needs text")
        return ParsedAction.Ok(build(text, cost))
    }

    private inline fun withRefCostText(args: List<String>, usage: String, build: (String, String, Quantity) -> SocialAction): ParsedAction {
        if (args.size < 3) return err("usage: $usage")
        val cost = quantityOrNull(args[1]) ?: return err("bad cost '${args[1]}'")
        val text = args.drop(2).joinToString(" ").trim()
        if (text.isEmpty()) return err("$usage — text is empty")
        return ParsedAction.Ok(build(args[0], text, cost))
    }

    private inline fun refThenCost(args: List<String>, usage: String, build: (String, Quantity) -> SocialAction): ParsedAction {
        if (args.size != 2) return err("usage: $usage")
        val cost = quantityOrNull(args[1]) ?: return err("bad cost '${args[1]}'")
        return ParsedAction.Ok(build(args[0], cost))
    }

    private fun err(message: String) = ParsedAction.Error(message)

    /** Parse an integer (`10`, `-3`) or a fraction (`1/2`) into a [Quantity]. */
    fun quantityOrNull(token: String): Quantity? {
        if ('/' in token) {
            val (n, d) = token.split('/', limit = 2)
            val num = n.toIntOrNull() ?: return null
            val den = d.toIntOrNull()?.takeIf { it != 0 } ?: return null
            return Quantity(num, den)
        }
        return token.toIntOrNull()?.let { Quantity(it) }
    }
}

/**
 * Resolve [action]'s references against [board] and perform it through the node's venue.
 *
 * Authoring routes through the same [SocialNode] verbs the Web uses, so the action appends one commitment and publishes
 * a new head. Unresolved references and venue rejections (e.g. replying through a route not addressed to this actor)
 * return [ActionOutcome.Failed] rather than throwing.
 */
fun SocialNode.perform(action: SocialAction, board: SocialBoard): ActionOutcome = try {
    when (action) {
        is SocialAction.PostText -> {
            val post = post(action.text, action.cost)
            ActionOutcome.Done("posted: \"${action.text}\"", socialRefOf(post.link, projector))
        }
        is SocialAction.Comment -> {
            val on = board.post(action.on) ?: return ActionOutcome.Failed("unknown post ${action.on}")
            val comment = comment(on, action.text, action.cost)
            ActionOutcome.Done("commented on ${action.on}", socialRefOf(comment.link, projector))
        }
        is SocialAction.Reply -> {
            val via = board.route(action.via) ?: return ActionOutcome.Failed(
                // `reply` takes an inbox-route ref; agents reach for it to answer a post and pass a post ref instead.
                // Point them at the right verb rather than the opaque "unknown route".
                if (board.post(action.via) != null)
                    "${action.via} is a post, not an inbox route — to respond to a post use: comment ${action.via} \"…\". " +
                        "reply is only for a route someone addressed to you (see your Inbox)."
                else "unknown route ${action.via}")
            val reply = reply(via, action.text, action.cost)
            ActionOutcome.Done("replied via ${action.via}", socialRefOf(reply.link, projector))
        }
        is SocialAction.Recommend -> {
            val p = board.post(action.post) ?: return ActionOutcome.Failed("unknown post ${action.post}")
            val to = board.actor(action.to) ?: return ActionOutcome.Failed("unknown actor ${action.to}")
            val route = recommend(p, to, action.cost)
            ActionOutcome.Done("recommended ${action.post} to ${action.to}", socialRefOf(route.link, projector))
        }
        is SocialAction.Endorse -> {
            val route = board.route(action.route) ?: return ActionOutcome.Failed("unknown route ${action.route}")
            endorse(route, action.cost); ActionOutcome.Done("endorsed ${action.route}", action.route)
        }
        is SocialAction.Back -> {
            val target = board.post(action.target) ?: board.route(action.target) ?: board.actor(action.target)
                ?: return ActionOutcome.Failed("unknown target ${action.target}")
            back(target, action.cost); ActionOutcome.Done("backed ${action.target}", action.target)
        }
    }
} catch (t: Throwable) {
    ActionOutcome.Failed(t.message ?: "action failed")
}
